import React, { ReactNode, useLayoutEffect, useRef, useState } from "react";
import AutoSizer from "../auto-sizer/AutoSizer";
import "./virtual-list.scss";
import { Virtuoso } from "react-virtuoso";

interface VirtualListProps<T> {
	items?: T[]; // optional
	renderItem: (item: T, index: number) => ReactNode;
	overscan?: number; // mapped to additionalTop/BottomItems if needed
	className?: string;
}

export const VirtualList = <T,>({
	items,
	renderItem,
	overscan = 5,
	className,
}: VirtualListProps<T>) => {
	const wrapRef = useRef<HTMLDivElement | null>(null);
	const [scrollParent, setScrollParent] = useState<HTMLElement | null>(null);
	const [scrollParentResolved, setScrollParentResolved] = useState(false);
	const safeItems = items ?? [];
	const wrapClassName = className ?? "virtual-list__wrap";

	useLayoutEffect(() => {
		const container = wrapRef.current?.closest(".window__body_list-inner");
		setScrollParent(container instanceof HTMLElement ? container : null);
		setScrollParentResolved(true);
	}, []);

	if (safeItems.length === 0) {
		return <div className={wrapClassName} />;
	}

	if (!scrollParentResolved) {
		return <div ref={wrapRef} className={wrapClassName} />;
	}

	const virtuoso = (
		<Virtuoso
			customScrollParent={scrollParent ?? undefined}
			data={safeItems}
			style={
				scrollParent ? undefined : { height: "100%", width: "100%" }
			}
			overscan={overscan}
			increaseViewportBy={{ top: 400, bottom: 400 }} // Глубина рендеринга при прокрутке
			itemContent={(index: number, item: T) => (
				<div className='virtual-list__item' data-index={index}>
					{renderItem(item, index)}
				</div>
			)}
		/>
	);

	if (scrollParent) {
		return (
			<div ref={wrapRef} className={wrapClassName}>
				{virtuoso}
			</div>
		);
	}

	return (
		<div ref={wrapRef} className={wrapClassName}>
			<AutoSizer>
				{(size: { width: number; height: number }) => {
					if (!size?.width || !size?.height) return null;

					return (
						<Virtuoso
							data={safeItems}
							style={{ height: size.height, width: size.width }}
							overscan={overscan}
							increaseViewportBy={{ top: 400, bottom: 400 }}
							itemContent={(index: number, item: T) => (
								<div className='virtual-list__item' data-index={index}>
									{renderItem(item, index)}
								</div>
							)}
						/>
					);
				}}
			</AutoSizer>
		</div>
	);
};

export default VirtualList;
